// pages/Paises.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Statistic,
  Divider,
  Upload,
  Card
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  ExportOutlined,
  GlobalOutlined,
  TranslationOutlined,
  FileTextOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Paises = () => {
  // Estados
  const [paises, setPaises] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [globalSearchText, setGlobalSearchText] = useState('');
  const [idiomas, setIdiomas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentPais, setCurrentPais] = useState(null);
  const [form] = Form.useForm();
  const [tableLoading, setTableLoading] = useState(false);
  
  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Cargar datos iniciales
  useEffect(() => {
    fetchPaises();
    fetchIdiomas();
  }, []);

  const fetchPaises = async () => {
    setTableLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/paises`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('📊 Datos recibidos del backend:', response.data.data);
      
      // IMPORTANTE: Asegurar que los datos están en minúsculas
      const datosTransformados = response.data.data.map(item => ({
        codigo: item.codigo || item.Codigo || '',
        descripcion: item.descripcion || item.Descripcion || '',
        idioma: item.idioma || item.Idioma || 'CAS',
        FicheroLOPD: item.FicheroLOPD || item.ficheroLOPD || '',
        ficherofirma: item.ficherofirma || item.FicheroFirma || ''
      }));
      
      console.log('✅ Datos transformados:', datosTransformados);
      
      setPaises(datosTransformados);
      setFilteredData(datosTransformados);
      
    } catch (error) {
      message.error('Error al cargar los países');
      console.error('Error fetchPaises:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchIdiomas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/paises/idiomas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIdiomas(response.data.data);
    } catch (error) {
      console.error('Error cargando idiomas:', error);
    }
  };

  // Función para búsqueda global
  const handleGlobalSearch = (searchText) => {
    setGlobalSearchText(searchText);
    
    if (!searchText.trim()) {
      // Si no hay texto, mostrar todos los registros
      setFilteredData(paises);
      return;
    }
    
    const lowerSearchText = searchText.toLowerCase().trim();
    
    // Buscar en todos los campos de cada registro
    const filtered = paises.filter(record => {
      return Object.keys(record).some(key => {
        const value = record[key];
        
        if (value === null || value === undefined) return false;
        
        const stringValue = String(value).toLowerCase();
        return stringValue.includes(lowerSearchText);
      });
    });
    
    setFilteredData(filtered);
  };

  // Configuración de columnas - USANDO MINÚSCULAS
  const columns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 100,
      sorter: (a, b) => (a.codigo || '').localeCompare(b.codigo || ''),
      sortOrder: sortedInfo.columnKey === 'codigo' && sortedInfo.order,
      fixed: 'left',
      render: (codigo) => (
        <Tag color="blue" style={{ fontWeight: 'bold' }}>
          {codigo || '-'}
        </Tag>
      ),
    },
    {
      title: 'País',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 200,
      sorter: (a, b) => (a.descripcion || '').localeCompare(b.descripcion || ''),
      sortOrder: sortedInfo.columnKey === 'descripcion' && sortedInfo.order,
      ellipsis: true,
      render: (descripcion) => descripcion || '-',
    },
    {
      title: 'Idioma',
      dataIndex: 'idioma',
      key: 'idioma',
      width: 100,
      sorter: (a, b) => (a.idioma || '').localeCompare(b.idioma || ''),
      sortOrder: sortedInfo.columnKey === 'idioma' && sortedInfo.order,
      filters: idiomas.map(idioma => ({
        text: idioma,
        value: idioma
      })),
      filteredValue: filteredInfo.idioma || null,
      onFilter: (value, record) => (record.idioma || '') === value,
      render: (idioma) => (
        <Tag color="blue" style={{ fontWeight: 'bold' }}>
          {idioma || 'CAS'}
        </Tag>
      ),
    },
    {
      title: 'Fichero LOPD',
      dataIndex: 'FicheroLOPD',
      key: 'FicheroLOPD',
      width: 150,
      sorter: (a, b) => (a.FicheroLOPD || '').localeCompare(b.FicheroLOPD || ''),
      sortOrder: sortedInfo.columnKey === 'FicheroLOPD' && sortedInfo.order,
      render: (fichero) => (
        <Tag color={fichero ? 'green' : 'default'}>
          {fichero || 'Sin LOPD'}
        </Tag>
      ),
    },
    {
      title: 'Fichero Firma',
      dataIndex: 'ficherofirma',
      key: 'ficherofirma',
      width: 150,
      sorter: (a, b) => (a.ficherofirma || '').localeCompare(b.ficherofirma || ''),
      sortOrder: sortedInfo.columnKey === 'ficherofirma' && sortedInfo.order,
      render: (fichero) => (
        <Tag color={fichero ? 'purple' : 'default'}>
          {fichero ? 'PDF Firma' : 'Sin firma'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar país?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.codigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
          
          <Tooltip title="Ver detalle">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Handlers para las acciones
  const handleCreate = () => {
    setModalType('create');
    setCurrentPais(null);
    form.resetFields();
    form.setFieldsValue({
      idioma: 'CAS',
      FicheroLOPD: 'LOPD',
      ficherofirma: 'PDFFirma.pdf'
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentPais(record);
    
    console.log("📝 Registro para editar:", record);
    
    // IMPORTANTE: Usar los nombres de campos en minúsculas
    form.setFieldsValue({
      codigo: record.codigo || '',
      descripcion: record.descripcion || '',
      idioma: record.idioma || 'CAS',
      FicheroLOPD: record.FicheroLOPD || 'LOPD',
      ficherofirma: record.ficherofirma || 'PDFFirma.pdf'
    });
    setModalVisible(true);
  };

  const handleView = (record) => {
    Modal.info({
      title: `Detalle de País: ${record.descripcion || 'Sin nombre'}`,
      width: 700,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <strong>Código:</strong>
            </Col>
            <Col span={18}>
              <Tag color="blue" style={{ fontSize: '14px', padding: '4px 8px' }}>
                {record.codigo || '-'}
              </Tag>
            </Col>
            
            <Col span={6}>
              <strong>País:</strong>
            </Col>
            <Col span={18}>
              <Text strong>{record.descripcion || '-'}</Text>
            </Col>
            
            <Col span={6}>
              <strong>Idioma:</strong>
            </Col>
            <Col span={18}>
              <Tag color="geekblue">
                {record.idioma || 'CAS'}
              </Tag>
            </Col>
            
            <Col span={24}>
              <Divider orientation="left">Documentación</Divider>
            </Col>
            
            <Col span={6}>
              <strong>Fichero LOPD:</strong>
            </Col>
            <Col span={18}>
              <Tag color={record.FicheroLOPD ? 'green' : 'default'}>
                <FileTextOutlined /> {record.FicheroLOPD || 'No especificado'}
              </Tag>
            </Col>
            
            <Col span={6}>
              <strong>Fichero Firma:</strong>
            </Col>
            <Col span={18}>
              <Tag color={record.ficherofirma ? 'purple' : 'default'}>
                <FilePdfOutlined /> {record.ficherofirma || 'No especificado'}
              </Tag>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleDelete = async (codigo) => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${BASE_URL}/api/paises/${codigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remover del estado local
      const nuevosPaises = paises.filter(p => p.codigo !== codigo);
      setPaises(nuevosPaises);
      
      // También actualizar filteredData
      const nuevoFilteredData = filteredData.filter(p => p.codigo !== codigo);
      setFilteredData(nuevoFilteredData);
      
      message.success('País eliminado correctamente');
      
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar el país');
      console.error('Error handleDelete:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      
      console.log('📝 Datos a guardar:', values);
      
      if (modalType === 'create') {
        const response = await axios.post(
          `${BASE_URL}/api/paises`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // IMPORTANTE: Transformar los datos recibidos a minúsculas
        const nuevoPais = {
          codigo: response.data.data.codigo || response.data.data.Codigo || '',
          descripcion: response.data.data.descripcion || response.data.data.Descripcion || '',
          idioma: response.data.data.idioma || response.data.data.Idioma || 'CAS',
          FicheroLOPD: response.data.data.FicheroLOPD || response.data.data.ficheroLOPD || '',
          ficherofirma: response.data.data.ficherofirma || response.data.data.FicheroFirma || ''
        };
        
        // Agregar al estado local
        setPaises([...paises, nuevoPais]);
        
        // Si hay búsqueda activa, actualizar filteredData también
        if (globalSearchText) {
          const lowerSearchText = globalSearchText.toLowerCase();
          const shouldShowInSearch = Object.keys(nuevoPais).some(key => {
            const value = nuevoPais[key];
            if (value === null || value === undefined) return false;
            return String(value).toLowerCase().includes(lowerSearchText);
          });
          
          if (shouldShowInSearch) {
            setFilteredData([...filteredData, nuevoPais]);
          }
        } else {
          setFilteredData([...filteredData, nuevoPais]);
        }
        
        message.success('País creado correctamente');
        
      } else {
        // Edición
        console.log("📝 Editando país:", currentPais.codigo);
        console.log("📝 Valores:", values);
        
        const response = await axios.put(
          `${BASE_URL}/api/paises/${currentPais.codigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Transformar los datos recibidos a minúsculas
        const paisActualizado = {
          codigo: response.data.data.codigo || response.data.data.Codigo || currentPais.codigo,
          descripcion: response.data.data.descripcion || response.data.data.Descripcion || '',
          idioma: response.data.data.idioma || response.data.data.Idioma || 'CAS',
          FicheroLOPD: response.data.data.FicheroLOPD || response.data.data.ficheroLOPD || '',
          ficherofirma: response.data.data.ficherofirma || response.data.data.FicheroFirma || ''
        };
        
        // Actualizar el estado local
        const nuevosPaises = paises.map(p => 
          p.codigo === currentPais.codigo ? paisActualizado : p
        );
        
        setPaises(nuevosPaises);
        
        // Actualizar filteredData
        const nuevoFilteredData = filteredData.map(p => 
          p.codigo === currentPais.codigo ? paisActualizado : p
        );
        
        setFilteredData(nuevoFilteredData);
        
        message.success('País actualizado correctamente');
      }
      
      // Cerrar modal y limpiar formulario
      setModalVisible(false);
      form.resetFields();
      
    } catch (error) {
      console.error('❌ Error en handleSave:', error);
      console.error('❌ Response:', error.response);
      
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error.response?.data?.details) {
        message.error(error.response.data.details);
      } else {
        message.error('Error al guardar los datos');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (pagination, filters, sorter) => {
    console.log('Filtros aplicados:', filters);
    
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    
    // Aplicar filtros de columnas
    let result = [...paises];
    
    // Aplicar filtros de Ant Design
    Object.keys(filters).forEach(key => {
      const filterValues = filters[key];
      if (filterValues && filterValues.length > 0) {
        result = result.filter(item => {
          const itemValue = item[key];
          
          if (key === 'idioma') {
            return filterValues.includes(itemValue);
          }
          
          return true;
        });
      }
    });
    
    // Aplicar ordenación
    if (sorter.field && sorter.order) {
      result.sort((a, b) => {
        const aVal = a[sorter.field] || '';
        const bVal = b[sorter.field] || '';
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          if (sorter.order === 'ascend') {
            return aVal.localeCompare(bVal);
          } else {
            return bVal.localeCompare(aVal);
          }
        }
        return 0;
      });
    }
    
    // Aplicar búsqueda global si existe
    if (globalSearchText) {
      const lowerSearchText = globalSearchText.toLowerCase().trim();
      result = result.filter(record => {
        return Object.keys(record).some(key => {
          const value = record[key];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(lowerSearchText);
        });
      });
    }
    
    setFilteredData(result);
  };

  const clearFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setGlobalSearchText('');
    setFilteredData(paises);
    message.success('Filtros y búsqueda limpiados');
  };

  // Función para exportar
  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Países" 
          value={paises.length}
          prefix={<GlobalOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Con idioma ESP" 
          value={paises.filter(p => p.idioma === 'CAS').length}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Con LOPD" 
          value={paises.filter(p => p.FicheroLOPD).length}
          valueStyle={{ color: '#faad14' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Con firma PDF" 
          value={paises.filter(p => p.ficherofirma).length}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
    </Row>
  );

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input.Search
          placeholder="Buscar por código, país, idioma, etc...."
          value={globalSearchText}
          style={{ width: 400 }}
          allowClear
          enterButton
          onChange={(e) => {
            setGlobalSearchText(e.target.value);
            // Si el usuario borra todo el texto, buscar inmediatamente
            if (e.target.value === '') {
              handleGlobalSearch('');
            }
          }}
          onSearch={handleGlobalSearch}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={clearFilters}
          size="small"
        >
          Limpiar
        </Button>
        <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
          <strong>{filteredData.length}</strong> registros
        </Tag>
      </Space>
    </div>
  );

  return (
    <>
      <PageContainer
        title="Gestión de Países"
        subtitle={`${paises.length} países registrados`}
        icon={<GlobalOutlined />}
        loading={tableLoading}
        onRefresh={fetchPaises}
        onCreate={handleCreate}
        onExport={handleExport}
        badgeCount={paises.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{paises.length}</strong> países
            </Tag>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}

        {searchBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="codigo"
          loading={tableLoading}
          onChange={handleChange}
          scroll={{ x: 1000 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar */}
      <Modal
        title={modalType === 'create' ? 'Nuevo País' : 'Editar País'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={700}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="codigo"
                label="Código"
                rules={[
                  { required: true, message: 'Por favor ingrese el código' },
                  { max: 3, message: 'El código debe tener máximo 3 caracteres' }
                ]}
              >
                <Input 
                  placeholder="Ej: ESP" 
                  disabled={modalType === 'edit'} 
                  maxLength={3}
                  showCount 
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="descripcion"
                label="Nombre del País"
                rules={[{ required: true, message: 'Por favor ingrese el nombre del país' }]}
              >
                <Input placeholder="Ej: España" maxLength={100} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="idioma"
                label="Idioma Principal"
                rules={[
                  { max: 3, message: 'El idioma debe tener máximo 3 caracteres' }
                ]}
              >
                <Select
                  showSearch
                  placeholder="Seleccione idioma"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                >
                  {idiomas.map(idioma => (
                    <Option key={idioma} value={idioma}>
                      {idioma}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="FicheroLOPD"
                label="Fichero LOPD"
                tooltip="Nombre del fichero de protección de datos"
              >
                <Input placeholder="Ej: LOPD" maxLength={100} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="ficherofirma"
                label="Fichero de Firma"
                tooltip="Nombre del fichero PDF con las firmas"
              >
                <Input placeholder="Ej: PDFFirma.pdf" maxLength={100} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default Paises;